import React from 'react';
import Rebates from '../component/partials/Rebates';

export const PRODUCT_DOMAINS = ['identifiers', 'media', 'pricing', 'info'];

export const PRICE_TYPES = {
  STANDARD: 'STANDARD',
  BASIC: 'BASIC',
  STRIKE_THROUGH: 'STRIKE_THROUGH',
  STARTING_AT: 'STARTING_AT',
  MAP_PRICE: 'MAP_PRICE',
  ECO_REBATE: 'ECO_REBATE',
  INSTANT_REBATE: 'INSTANT_REBATE',
  BULK_PRICE: 'BULK_PRICE',
  UNIT_AND_CASE: 'UNIT_AND_CASE',
  LIMIT_PER_ORDER: 'LIMIT_PER_ORDER',
  RANGE_PRICE: 'RANGE_PRICE',
  BRIO: 'BRIO',
  LIVE_GOODS: 'LIVE_GOODS',
  NO_DETAILS: 'NO_DETAILS'
};

export const EXCHANGE_PROMOTION_TAG = 'EXCHANGE';

const helpers = {
  getPricingObject: ({
    skuModel, displayEachUom, omitDetails, basic, disableRangePricing = false
  }) => {
    const { pricing } = skuModel;
    const ret = {};
    let priceType = PRICE_TYPES.STANDARD;

    if (!pricing) {
      return { _price: ret, type: '' };
    }

    const {
      alternatePriceDisplay,
      alternatePricingDisplay,
      unitWasPrice,
      unitNowPrice,
      unitOfMeasureCoverage,
      pricingMessage,
      specialBuyPrice,
      nowPrice,
      wasPrice,
      minPriceRange,
      maxPriceRange,
      wasMinPriceRange,
      wasMaxPriceRange,
      percentageOff,
      dollarOff,
      installationServiceType,
      productSubType,
      savingsCenter,
      hidePrice,
      bulkPrice,
      bulkPriceThresholdQty,
      caseUnitUom,
      uom,
      rebates,
      storePromotion,
      quantityLimit,
      mapAboveOriginalPrice
    } = pricing;

    if (omitDetails) {
      if (pricingMessage) {
        if (mapAboveOriginalPrice) {
          return { _price: ret, type: PRICE_TYPES.NO_DETAILS };
        }

      } else if (alternatePriceDisplay || alternatePricingDisplay) {
        if (uom === 'sq. ft.') {
          if (unitWasPrice !== unitNowPrice) {
            ret.price = helpers.toFixed(unitNowPrice, 2);
          } else {
            ret.price = helpers.toFixed(unitWasPrice, 2);
          }
        } else if (uom !== 'sq. ft.') {
          if (wasPrice !== nowPrice) {
            ret.price = helpers.toFixed(nowPrice, 2);
          } else {
            ret.price = helpers.toFixed(wasPrice, 2);
          }
        }
      } else if (percentageOff) {
        ret.save = helpers.savingsPrice(
          percentageOff, dollarOff,
          wasPrice, specialBuyPrice, nowPrice
        );
        ret.price = helpers.toFixed(nowPrice, 2);
        ret.pricingValues = {
          wasPrice: helpers.toFixed(wasPrice, 2),
          nowPrice: ret.price
        };
      } else if (!pricingMessage && !specialBuyPrice) {
        ret.price = helpers.toFixed(nowPrice, 2);
      }
      ret.uom = uom ? uom.toLowerCase() : '';
      ret.priceBadge = helpers.priceBadge(savingsCenter);
      return { _price: ret, type: PRICE_TYPES.NO_DETAILS };
    }

    // Basic price
    if (basic) {
      if (pricingMessage) {
        if (mapAboveOriginalPrice) {
          return {
            _price: ret,
            type: PRICE_TYPES.NO_DETAILS
          };
        }
      } else {
        ret.price = helpers.toFixed(nowPrice, 2);
        return {
          _price: ret,
          type: PRICE_TYPES.BASIC
        };
      }
    }

    // Range Price
    if (!disableRangePricing && pricing && minPriceRange && maxPriceRange
      && minPriceRange !== maxPriceRange) {
      const minPrice = parseFloat(minPriceRange);
      const maxPrice = parseFloat(maxPriceRange);
      ret.minPrice = helpers.toFixed(minPriceRange, 2);
      ret.maxPrice = helpers.toFixed(maxPriceRange, 2);
      // strike-through range
      if (wasMinPriceRange && wasMaxPriceRange) {
        const wasMin = parseFloat(wasMinPriceRange);
        const wasMax = parseFloat(wasMaxPriceRange);
        if (wasMin > minPrice && wasMax > maxPrice) {
          if (wasMin !== wasMax) {
            ret.wasMinPrice = helpers.toFixed(wasMinPriceRange, 2);
            ret.wasMaxPrice = helpers.toFixed(wasMaxPriceRange, 2);
          } else {
            ret.wasMinPrice = helpers.toFixed(wasMinPriceRange, 2);
          }
        }
      }
      // savings %
      if (percentageOff && percentageOff > 0) {
        ret.percentageOff = helpers.toFixed(percentageOff);
      }

      return { _price: ret, type: PRICE_TYPES.RANGE_PRICE };
    }

    // BRIO SKU
    if (pricing
      && ((installationServiceType && installationServiceType === 'SFI')
        || (productSubType && productSubType.name === 'SFI'))
    ) {
      ret.isBRIOSku = true;
      return { _price: ret, type: PRICE_TYPES.BRIO };
    }

    if (hidePrice) {
      return { _price: {}, type: PRICE_TYPES.LIVE_GOODS };
    }

    // Price Badges
    ret.priceBadge = helpers.priceBadge(savingsCenter);

    // Bulk pricing
    if (bulkPrice && bulkPriceThresholdQty) {
      ret.bulkPrice = bulkPrice;
      ret.bulkPriceThresholdQty = bulkPriceThresholdQty;
      ret.priceBadge = 'Bulk-Price';
      priceType = PRICE_TYPES.BULK_PRICE;
    }

    // Save
    ret.save = helpers.savingsPrice(
      percentageOff, dollarOff,
      wasPrice, specialBuyPrice, nowPrice
    );
    // Starting at price
    if (pricingMessage && pricingMessage === 'Starting at') {
      ret.pricingMessage = pricingMessage;
      if (wasPrice) {
        ret.price = helpers.toFixed(wasPrice, 2);
      }
      if (nowPrice) {
        ret.price = helpers.toFixed(nowPrice, 2);
      }
      if (wasPrice && nowPrice && nowPrice !== wasPrice) {
        ret.pricingValues = {
          wasPrice: helpers.toFixed(wasPrice, 2),
          nowPrice: helpers.toFixed(nowPrice, 2)
        };
      }

      ret.uom = uom ? uom.toLowerCase() : '';
      return { _price: ret, type: PRICE_TYPES.STARTING_AT };
    }

    // MAP pricing
    if (pricingMessage) {
      if (mapAboveOriginalPrice) {
        ret.pricingMessage = 'See Low Price in Cart';
        priceType = PRICE_TYPES.MAP_PRICE;
      } else if (typeof mapAboveOriginalPrice !== 'undefined') {
        if (wasPrice && nowPrice !== wasPrice) {
          ret.pricingValues = {
            wasPrice: helpers.toFixed(wasPrice, 2),
            nowPrice: ''
          };
          ret.pricingMessage = 'See Lower Price in Cart';
        } else {
          ret.pricingMessage = 'See Low Price in Cart';
        }
        return { _price: ret, type: PRICE_TYPES.MAP_PRICE };
      }
    } else if (specialBuyPrice
      && wasPrice >= specialBuyPrice && specialBuyPrice > nowPrice) {
      ret.mapPrice = {
        wasPrice: helpers.toFixed(wasPrice),
        pricingMessage: 'See Lower Price in Cart',
        hasStrikeThrough: true
      };
      if (wasPrice && nowPrice && nowPrice !== wasPrice) {
        ret.pricingValues = {
          wasPrice: helpers.toFixed(wasPrice, 2),
          nowPrice: ''
        };
      }
      ret.pricingMessage = 'See Lower Price in Cart';
      priceType = PRICE_TYPES.MAP_PRICE;
    } else if (mapAboveOriginalPrice) {
      ret.pricingMessage = 'See Low Price in Cart';
      priceType = PRICE_TYPES.MAP_PRICE;
    }

    // Unit pricing/Alternate pricing
    const mapViolation = !pricingMessage && !mapAboveOriginalPrice;
    const hasAlternatePrice = !!alternatePriceDisplay || !!alternatePricingDisplay;
    const hasQuantityLimit = !!quantityLimit;
    const hasUnitOfMeasureCoverage = !!unitOfMeasureCoverage;
    const bulbProductScenario = caseUnitUom === 'bulb' && hasUnitOfMeasureCoverage;
    const bulbProductCheck = bulbProductScenario && !hasQuantityLimit;
    if (mapViolation && (hasAlternatePrice || bulbProductCheck)) {
      if (unitWasPrice && unitNowPrice && unitWasPrice !== unitNowPrice) {
        ret.unitPricingValues = {
          wasPrice: helpers.toFixed(unitWasPrice, 2),
          nowPrice: helpers.toFixed(unitNowPrice, 2)
        };
      }
      if (wasPrice && nowPrice && nowPrice !== wasPrice) {
        ret.pricingValues = {
          wasPrice: helpers.toFixed(wasPrice, 2),
          nowPrice: helpers.toFixed(nowPrice, 2)
        };
      }

      ret.price = helpers.toFixed(unitNowPrice, 2);

      if (caseUnitUom) {
        ret.uom = caseUnitUom.toLowerCase();
      }
      if (percentageOff > 0) {
        ret.save = {
          dollarOff: helpers.toFixed(dollarOff, 2),
          percentageOff: helpers.toFixed(percentageOff)
        };
      }
      if (uom) {
        ret.alternatePrice = nowPrice;
        ret.alternateUom = uom.toLowerCase();
      }
      if (rebates && rebates.hasEcoRebates) {
        ret.hasEcoRebates = rebates.hasEcoRebates;
      }
      return { _price: ret, type: PRICE_TYPES.UNIT_AND_CASE };
    }

    // was pricing
    if (nowPrice && wasPrice && nowPrice !== wasPrice) {
      ret.pricingValues = {
        wasPrice: helpers.toFixed(wasPrice, 2),
        nowPrice: helpers.toFixed(nowPrice, 2)
      };
      if (priceType === PRICE_TYPES.STANDARD) {
        priceType = PRICE_TYPES.STRIKE_THROUGH;
      }
    }

    ret.price = '';
    if (wasPrice) {
      ret.price = helpers.toFixed(wasPrice, 2);
    } else if (nowPrice) {
      ret.price = helpers.toFixed(nowPrice, 2);
    }

    if (displayEachUom && uom) {
      ret.uom = uom.toLowerCase();
    } else if (uom && uom !== 'each') {
      ret.uom = uom.toLowerCase();
    }

    // Final Price Type Logic
    if (rebates && rebates.hasEcoRebates) {
      ret.hasEcoRebates = rebates.hasEcoRebates;
      priceType = PRICE_TYPES.ECO_REBATE;
    }
    if (storePromotion && storePromotion.longDescription) {
      priceType = PRICE_TYPES.INSTANT_REBATE;
    }
    if (hasQuantityLimit) {
      priceType = PRICE_TYPES.LIMIT_PER_ORDER;
    }
    return { _price: ret, type: priceType };
  },
  toFixed: (price, n) => {
    if (!price) {
      return '0';
    }
    if (typeof price === 'string') {
      return parseFloat(price).toFixed(n);
    }
    return price.toFixed(n);
  },
  parsePriceValue: (price) => {
    let ret = {};
    if (!price) {
      return '';
    }
    const priceSplit = price.toString().split('.');
    ret.dollars = priceSplit[0]; // eslint-disable-line
    ret.cents = priceSplit[1] || '00';
    if (ret.cents.length === 1) {
      ret.cents += '0';
    }
    return ret;
  },
  parseUom: (uom, isDisplayEachUom) => {
    let parsedUom = `/${uom}`;
    if (uom === 'each') {
      if (isDisplayEachUom) {
        parsedUom = '/each';
      } else {
        parsedUom = '';
      }
    } else if (uom.indexOf('pallet(') > -1) {
      parsedUom = '/pallet';
    }
    return parsedUom;
  },
  handlePriceParsing: (priceToParse, skipFormat) => {
    const retPrice = helpers.parsePriceValue(priceToParse);
    if (retPrice.dollars === '0' && !skipFormat) {
      return (<span>{retPrice.cents}<span className="price__cent-format">¢</span></span>);
    }
    if (retPrice.dollars === '0' && skipFormat) {
      return (<span>{retPrice.cents}¢</span>);
    }
    return (<span>${retPrice.dollars}.{retPrice.cents}</span>);
  },
  triggerAnalytics: (tooltipLocation) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('price.click', { location: tooltipLocation });
    }
  },
  savingsPrice: (percentageOff, dollarOff, wasPrice, specialBuyPrice, nowPrice) => {
    // Save
    let ret = {};
    const isOnSale = !(wasPrice >= specialBuyPrice && specialBuyPrice > nowPrice);
    if (percentageOff > 0 && isOnSale) {
      ret.save = {
        dollarOff: helpers.toFixed(dollarOff, 2),
        percentageOff: helpers.toFixed(percentageOff)
      };
    }
    return ret.save;
  },
  priceBadge: (savingsCenter) => {
    // Price Badges
    let ret = {};
    const badge = savingsCenter && savingsCenter.toLowerCase();
    if (badge === 'overstock') {
      ret.priceBadge = 'Over-Stock';
    } else if (badge === 'special buys') {
      ret.priceBadge = 'Special-Buy';
    } else if (badge === 'new lower prices') {
      ret.priceBadge = 'New-Lower-Price';
    } else if (badge === 'clearance') {
      ret.priceBadge = 'Clearance';
    }
    return ret.priceBadge;
  },
  getRebates: (hideRebates, hasEcoRebates, itemId, zipCode, large, multi = false) => {
    const showRebates = !hideRebates && hasEcoRebates && zipCode;
    if (showRebates) {
      return (
        <Rebates
          itemId={itemId}
          zipCode={zipCode}
          optionalText="Get up to {{ totalAmount ? totalAmount + ' in ' : offerCount }} Rebate{{ totalAmount || offerCount > 1 ? 's' : ''}} for {{ zipCode }}" // eslint-disable-line
          rebateClasses={large ? '' : 'price__ecorebates'}
          multi={multi}
        />
      );
    }
    return null;
  },
  getWasPrice: (pricingValues) => {
    if (pricingValues) {
      return <span className="u__strike">{helpers.handlePriceParsing(pricingValues.wasPrice, true)}</span>;
    }
    return null;
  },
  getWasPriceRange: (wasMinPrice, wasMaxPrice) => {
    if (wasMinPrice && wasMaxPrice) {
      return (
        <span className="u__strike">
          {helpers.handlePriceParsing(wasMinPrice, true)}
          <span> - {helpers.handlePriceParsing(wasMaxPrice, true)}</span>
        </span>
      );
    }
    return null;
  },
  getBulkPrice: (hideBulkPrice, bulkPrice, bulkPriceThresholdQty, uom = null, displayEachUom = false) => {
    if (!hideBulkPrice && bulkPrice) {
      return (
        <div className="u__text--success">
          {/* eslint-disable max-len */}
          Buy <span className="u__bold">{bulkPriceThresholdQty}</span> or more <span className="u__bold">{helpers.handlePriceParsing(bulkPrice, true)}</span>
          {uom && <span> {uom && helpers.parseUom(uom, displayEachUom)}</span>}
        </div>
      );
    }
    return null;
  },
  getLimitPerOrder: (hideLimitPerOrder, quantityLimit, limitClass) => {
    if (!hideLimitPerOrder && !!quantityLimit) {
      return <div className={limitClass}>Limit {quantityLimit} per order</div>;
    }
    return null;
  },
  getSavingsText: (isInline, hideSavingsText, save, showDollarOff = true, savePriceClass = '') => {
    const Element = isInline ? 'span' : 'div';
    if (!hideSavingsText && save) {
      return (
        <Element className={`u__text--success ${savePriceClass}`}> Save
          {
            showDollarOff
              ? (<> { helpers.handlePriceParsing(save.dollarOff, true) } ({save.percentageOff}%)</>)
              : (<> {save.percentageOff}%</>)
          }
        </Element>
      );
    }
    return null;
  }
};

export default helpers;